import { AutoCoderPipeline } from '../types/gotypes'

/**
 * The information architecture of the pipeline page.
 *
 * These used to be tabs along the top of a modal. They are now the left-hand
 * nav of `/autocoder/:customer/:job/pipelines/:pipelineId`, and the section is
 * carried in the URL (`?section=`) so a view is linkable.
 *
 * Every pipeline is a coding pipeline, so every pipeline gets every section.
 * (Until F1 a `scrape_only` pipeline hid the coding-shaped sections; that mode
 * is retired — a scrape without coding is now an import receipt with no
 * pipeline at all, and it has no page here.) The resolver below stays because
 * `?section=` still has to be validated against the known ids.
 */
export type PipelineSectionId =
  | 'throughput'
  | 'batches'
  | 'results'
  | 'frameworks'
  | 'suggestions'
  | 'settings'
  | 'status'

/**
 * Declaration order IS nav order. Throughput leads: it is the landing view.
 *
 * `imports` became `batches`: an import receipt is PROVENANCE and is now
 * job-wide in the shell's Data tab (a receipt need not belong to any pipeline
 * at all), while a coding batch is this pipeline's own work and has nowhere
 * else to live.
 */
export const PIPELINE_SECTION_IDS: PipelineSectionId[] = [
  'throughput',
  'batches',
  'results',
  'frameworks',
  // 'suggestions' — HIDDEN, not deleted (Kai, 2026-08-14). The id, its label and
  // its count plumbing all survive, so putting it back is this one line. What is
  // gone is the nav entry: the section is a leftover from the framework-building
  // era and every pipeline that reaches this page is already coding.
  //
  // `resolvePipelineSection` validates `?section=` against THIS list, so an old
  // deep link lands on Throughput rather than a blank pane — which is why the
  // section is removed here rather than by hiding the button at the call site.
  'settings',
  'status',
]

export interface PipelineSection {
  id: PipelineSectionId
  label: string
}

const BASE_LABELS: Record<PipelineSectionId, string> = {
  throughput: 'Throughput',
  batches: 'Batches',
  results: 'Results',
  frameworks: 'Frameworks',
  suggestions: 'Suggestions',
  settings: 'Settings',
  // "Import Status" described neither of the two things left in this section
  // once Throughput took over progress (T2/T3) — and it was wrong twice over:
  // this is coding, not importing, and `imported` is a retired one-shot state.
  // What remains is the end of the pipeline: what failed to come out, and
  // pushing what did into Carbon. The ID stays `status` so existing
  // `?section=status` links keep working.
  status: 'Output',
}

export interface PipelineSectionOptions {
  /** Suggestion count, surfaced in the nav label when non-zero. */
  suggestions?: number
}

export const pipelineSections = (
  _pipeline?: AutoCoderPipeline,
  options: PipelineSectionOptions = {},
): PipelineSection[] => {
  return PIPELINE_SECTION_IDS.map(id => ({
    id,
    label: id === 'suggestions' && options.suggestions
      ? `${BASE_LABELS[id]} (${options.suggestions})`
      : BASE_LABELS[id],
  }))
}

/**
 * Throughput is the landing view — "the page that gives us a sense of what the
 * pipeline is doing". The first visible section wins if it is ever absent,
 * rather than a hard-coded second choice.
 */
export const defaultPipelineSection = (pipeline?: AutoCoderPipeline): PipelineSectionId => {
  const sections = pipelineSections(pipeline)
  const throughput = sections.find(s => s.id === 'throughput')
  return throughput ? throughput.id : sections[0].id
}

/**
 * Resolve the `?section=` URL param against what this pipeline actually has.
 * Anything absent, unknown, or hidden lands on the default — a deep link must
 * never render a blank pane.
 */
export const resolvePipelineSection = (
  section: string | undefined,
  pipeline?: AutoCoderPipeline,
): PipelineSectionId => {
  const sections = pipelineSections(pipeline)
  const match = sections.find(s => s.id === section)
  return match ? match.id : defaultPipelineSection(pipeline)
}
